// material-ui
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';

// project-imports
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// ==============================|| OPS DECK TEAL SIDEBAR - SHARED TOKENS ||============================== //
//
// The sidebar is a deep teal "ink" in BOTH light and dark theme modes — the dark
// end of the Ops Deck brand ramp (#0E9498) — so it reads as one family with the
// white/teal content. Colours are hard-coded rather than read from the palette,
// which flips by mode.

export const NAV_BG = '#0C2B2E'; // deep teal ink surface (brand, darkened)
const NAV_TEXT = '#AED6D7'; // idle item label + icon (soft teal-grey)
const NAV_CAPTION = '#6FAEB0'; // section captions (dimmer teal)
const NAV_HOVER_BG = 'rgba(255, 255, 255, 0.06)';
const NAV_SELECTED_BG = '#0E9498'; // solid brand-teal highlight — white content pops on it
const LOGO_ACCENT = '#43D0D4'; // "Store" wordmark accent, brightened for the dark header
export const NAV_BORDER = 'rgba(255, 255, 255, 0.07)';

// Recolours the nav + logo for the dark surface. Scoped to `.simplebar-content`
// (the menu) and the header logo Box; descendant selectors out-specify the inline
// `sx` colours the nav components set, so no per-component edits are needed.
export const navStyles = {
  '& .simplebar-content .MuiTypography-root': { color: NAV_TEXT },
  '& .simplebar-content .MuiListItemIcon-root': { color: NAV_TEXT },
  '& .simplebar-content .MuiTypography-h5': { color: NAV_CAPTION },
  '& .simplebar-content .MuiListItemButton-root': {
    transition: 'background-color 0.2s ease'
  },
  '& .simplebar-content .MuiListItemButton-root:hover': { backgroundColor: NAV_HOVER_BG },
  // selected item content is white in every state for contrast against the indigo pill
  '& .simplebar-content .Mui-selected .MuiTypography-root': { color: '#fff' },
  '& .simplebar-content .Mui-selected .MuiListItemIcon-root': { color: '#fff' },
  // logo wordmark on the dark header: "Hexa" white, "UI" accent lightened
  '& .MuiDrawer-paper > .MuiBox-root .MuiTypography-root': { color: '#fff' },
  '& .MuiDrawer-paper > .MuiBox-root .MuiTypography-root .MuiBox-root': { color: LOGO_ACCENT }
};

// Expanded drawer (desktop open + mobile temporary): the whole row becomes the indigo pill.
export const navSelectedExpanded = {
  '& .simplebar-content .Mui-selected': { backgroundColor: `${NAV_SELECTED_BG} !important` },
  '& .simplebar-content .Mui-selected:hover': { backgroundColor: `${NAV_SELECTED_BG} !important` }
};

// Collapsed mini drawer: the active icon sits in a subtle frosted chip (darker than a
// solid indigo fill) so the bright white icon stays clearly visible — a solid fill made
// box-shaped icons merge into the chip. Replaces the near-white default box.
const NAV_SELECTED_CHIP = 'rgba(255, 255, 255, 0.16)';
const navSelectedCollapsed = {
  '& .simplebar-content .Mui-selected .MuiListItemIcon-root': { backgroundColor: `${NAV_SELECTED_CHIP} !important`, color: '#fff' },
  '& .simplebar-content .Mui-selected .MuiListItemIcon-root:hover': { backgroundColor: `${NAV_SELECTED_CHIP} !important` }
};

const openedMixin = (theme) => ({
  backgroundColor: NAV_BG,
  width: DRAWER_WIDTH,
  borderRight: `1px solid ${NAV_BORDER}`,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  boxShadow: 'none'
});

const closedMixin = (theme) => ({
  backgroundColor: NAV_BG,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: MINI_DRAWER_WIDTH,
  borderRight: `1px solid ${NAV_BORDER}`,
  boxShadow: theme.customShadows.z1
});

// ==============================|| DRAWER - MINI STYLED ||============================== //

const MiniDrawerStyled = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...navStyles,
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
    ...navSelectedExpanded
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
    ...navSelectedCollapsed
  })
}));

export default MiniDrawerStyled;
